"use client";

import { type ReactNode, useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { translations } from "../translations";
import { getCurrentLocale, type Locale } from "@/shared/utils/locale-cookies";

type I18nProviderProps = {
  children: ReactNode;
  locale?: Locale;
  pathname?: string;
};

// Create a fresh i18n instance for each provider to avoid conflicts
function createI18nInstance(locale: Locale) {
  const instance = i18n.createInstance();

  instance.use(initReactI18next);
  void instance.init({
    resources: translations,
    fallbackLng: "en",
    load: "languageOnly",
    supportedLngs: ["en", "ru", "pt"],
    interpolation: { escapeValue: false },
    lng: locale,
  });

  return instance;
}

export function I18nProvider({
  children,
  locale,
  pathname,
}: I18nProviderProps) {
  const [currentLocale, setCurrentLocale] = useState<Locale>(() => {
    // If locale is provided (SSG), use it
    if (locale) {
      console.log(`I18nProvider: Using SSG locale ${locale}`);
      return locale;
    }
    // Otherwise, get from cookie or URL
    const detectedLocale = getCurrentLocale(pathname);
    console.log(
      `I18nProvider: Detected locale ${detectedLocale} for pathname ${pathname}`,
    );
    return detectedLocale;
  });

  // Create i18n instance with the determined locale
  const i18nInstance = createI18nInstance(currentLocale);

  useEffect(() => {
    if (locale) {
      // SSG case - use provided locale
      console.log(`I18nProvider: SSG case - using locale ${locale}`);
      setCurrentLocale(locale);
    } else {
      // SPA case - use cookie locale
      const cookieLocale = getCurrentLocale(pathname);
      console.log(
        `I18nProvider: SPA case - using locale ${cookieLocale} for pathname ${pathname}`,
      );
      setCurrentLocale(cookieLocale);
    }
  }, [locale, pathname]);

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
}
